import {
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    IProjectionOutput,
    ITextPrimitive,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

/** A box in pane-local pixels. */
export interface IPxBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

/** The points a polyline reaches, a bulged segment adding the top of its arc. */
function polyPoints(prim: IPolyPrimitive): IPoint[] {
    const points: IPoint[] = [...prim.points];
    const bulges: readonly number[] = prim.bulges ?? [];
    const count: number = prim.points.length;
    bulges.forEach((bulge: number, index: number) => {
        if (bulge === 0 || (!prim.closed && index === count - 1)) {
            return;
        }
        const from: IPoint = prim.points[index];
        const to: IPoint = prim.points[(index + 1) % count];
        const dx: number = to[0] - from[0];
        const dy: number = to[1] - from[1];
        // The sagitta of a bulged chord is bulge · chord / 2, off the chord's middle.
        const sag: number = bulge / 2;
        points.push([(from[0] + to[0]) / 2 + dy * sag, (from[1] + to[1]) / 2 - dx * sag]);
    });
    return points;
}

function textLeft(align: ETextAlign, width: number): number {
    if (align === ETextAlign.Left) {
        return 0;
    }
    return align === ETextAlign.Center ? -width / 2 : -width;
}

function textTop(baseline: ETextBaseline, height: number): number {
    if (baseline === ETextBaseline.Top) {
        return 0;
    }
    return baseline === ETextBaseline.Middle ? -height / 2 : -height;
}

/** The four corners of a text's box, turned with the text around its anchor. */
function textPoints(prim: ITextPrimitive, measurer: ITextMeasurer): IPoint[] {
    const width: number = measurer.measure(prim.text, prim.font);
    const height: number = prim.font.size;
    const left: number = textLeft(prim.align, width);
    const top: number = textTop(prim.baseline, height);
    const cos: number = Math.cos(prim.rotation ?? 0);
    const sin: number = Math.sin(prim.rotation ?? 0);
    const corners: IPoint[] = [
        [left, top],
        [left + width, top],
        [left + width, top + height],
        [left, top + height],
    ];
    return corners.map((corner: IPoint): IPoint => [
        prim.at[0] + corner[0] * cos - corner[1] * sin,
        prim.at[1] + corner[0] * sin + corner[1] * cos,
    ]);
}

function primitivePoints(prim: IPlanPrimitive, measurer: ITextMeasurer): IPoint[] {
    switch (prim.kind) {
        case EPrimitiveKind.Poly:
            return polyPoints(prim);
        case EPrimitiveKind.Text:
            return textPoints(prim, measurer);
        case EPrimitiveKind.Dim:
            return [
                [Math.min(prim.from[0], prim.to[0]) - prim.tick, Math.min(prim.from[1], prim.to[1]) - prim.tick],
                [Math.max(prim.from[0], prim.to[0]) + prim.tick, Math.max(prim.from[1], prim.to[1]) + prim.tick],
                [prim.labelBox[0], prim.labelBox[1]],
                [prim.labelBox[2], prim.labelBox[3]],
            ];
        case EPrimitiveKind.Arc:
            return [
                [prim.center[0] - prim.radius, prim.center[1] - prim.radius],
                [prim.center[0] + prim.radius, prim.center[1] + prim.radius],
            ];
        case EPrimitiveKind.Hatch:
            return [
                [prim.rect.x, prim.rect.y],
                [prim.rect.x + prim.rect.w, prim.rect.y + prim.rect.h],
            ];
    }
}

function boundsOf(points: readonly IPoint[]): IPxBounds {
    const xs: number[] = points.map((point: IPoint): number => point[0]);
    const ys: number[] = points.map((point: IPoint): number => point[1]);
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

/** The axis-aligned box of a text on the screen, its rotation included. */
export function textBox(prim: ITextPrimitive, measurer: ITextMeasurer): IPxBounds {
    return boundsOf(textPoints(prim, measurer));
}

/** The box every primitive of a projection fits in, labels included; null when it draws nothing. */
export function projectionBounds(output: IProjectionOutput, measurer: ITextMeasurer): IPxBounds | null {
    const points: IPoint[] = output.primitives.flatMap((prim: IPlanPrimitive): IPoint[] => primitivePoints(prim, measurer));
    if (points.length === 0) {
        return null;
    }
    let minX: number = Infinity;
    let minY: number = Infinity;
    let maxX: number = -Infinity;
    let maxY: number = -Infinity;
    for (const point of points) {
        minX = Math.min(minX, point[0]);
        minY = Math.min(minY, point[1]);
        maxX = Math.max(maxX, point[0]);
        maxY = Math.max(maxY, point[1]);
    }
    return { minX, minY, maxX, maxY };
}

function shift(point: IPoint, dx: number, dy: number): IPoint {
    return [point[0] + dx, point[1] + dy];
}

function translatePrimitive(prim: IPlanPrimitive, dx: number, dy: number): IPlanPrimitive {
    switch (prim.kind) {
        case EPrimitiveKind.Poly:
            return { ...prim, points: prim.points.map((point: IPoint): IPoint => shift(point, dx, dy)) };
        case EPrimitiveKind.Text:
            return { ...prim, at: shift(prim.at, dx, dy) };
        case EPrimitiveKind.Dim:
            return {
                ...prim,
                from: shift(prim.from, dx, dy),
                to: shift(prim.to, dx, dy),
                labelBox: [prim.labelBox[0] + dx, prim.labelBox[1] + dy, prim.labelBox[2] + dx, prim.labelBox[3] + dy],
            };
        case EPrimitiveKind.Arc:
            return { ...prim, center: shift(prim.center, dx, dy) };
        case EPrimitiveKind.Hatch:
            return { ...prim, rect: { ...prim.rect, x: prim.rect.x + dx, y: prim.rect.y + dy } };
    }
}

/** The same projection moved by (dx, dy) pixels; its millimetre origin moves with it. */
export function translateProjection(output: IProjectionOutput, dx: number, dy: number): IProjectionOutput {
    if (dx === 0 && dy === 0) {
        return output;
    }
    return {
        primitives: output.primitives.map((prim: IPlanPrimitive): IPlanPrimitive => translatePrimitive(prim, dx, dy)),
        pxPerMm: output.pxPerMm,
        origin: { x: output.origin.x + dx, y: output.origin.y + dy },
    };
}

/** The box of every label a projection writes — its texts and its dimension labels — with its text. */
export function textBoxes(output: IProjectionOutput, measurer: ITextMeasurer): { text: string; box: IPxBounds }[] {
    const boxes: { text: string; box: IPxBounds }[] = [];
    for (const prim of output.primitives) {
        if (prim.kind === EPrimitiveKind.Text) {
            boxes.push({ text: prim.text, box: textBox(prim, measurer) });
        } else if (prim.kind === EPrimitiveKind.Dim) {
            boxes.push({
                text: prim.label,
                box: { minX: prim.labelBox[0], minY: prim.labelBox[1], maxX: prim.labelBox[2], maxY: prim.labelBox[3] },
            });
        } else {
            /* no-op: only texts and dimension labels carry words */
        }
    }
    return boxes;
}

/** Whether two boxes share more than `slack` pixels in both directions. */
export function boxesOverlap(a: IPxBounds, b: IPxBounds, slack: number = 0.5): boolean {
    return a.minX + slack < b.maxX && b.minX + slack < a.maxX && a.minY + slack < b.maxY && b.minY + slack < a.maxY;
}
